import React, { useState, useEffect } from 'react';
import {
  Plug, Search, Plus, CheckCircle,
  ExternalLink, Play, Pause, Square, Trash2,
  Zap, ArrowRight, ChevronRight, Mail, Server, MessageCircle, FolderOpen,
  Activity, Clock, X, Terminal, Info
} from 'lucide-react';
import Sidebar from '../components/ui/Sidebar';
import { toast } from 'sonner';
import * as integrationsService from '../../services/integrationsService';
import type { IntegrationComponent, Integration } from '../../services/integrationsService';

// Component icons mapping
const componentIcons: Record<string, React.ReactNode> = {
  ftp: <Server size={24} className="text-blue-600" />,
  imaps: <Mail size={24} className="text-red-500" />,
  sftp: <FolderOpen size={24} className="text-green-600" />,
  telegram: <MessageCircle size={24} className="text-sky-500" />,
};

const componentColors: Record<string, { bg: string; border: string; text: string }> = {
  ftp: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  imaps: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
  sftp: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  telegram: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700' },
};

type ViewMode = 'home' | 'list' | 'config';

const IntegrationsPage: React.FC = () => {
  // State
  const [viewMode, setViewMode] = useState<ViewMode>('home');
  const [components, setComponents] = useState<IntegrationComponent[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalIntegrations, setTotalIntegrations] = useState(0);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [logs, setLogs] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<string>('');

  // Form
  const [newIntegration, setNewIntegration] = useState({ name: '', description: '' });

  // Load components on mount
  useEffect(() => {
    loadComponents();
  }, []);

  const loadComponents = async () => {
    try {
      setLoading(true);
      const data = await integrationsService.getAllComponents();
      setComponents(data);
    } catch (error) {
      console.error('Error loading components:', error);
      // Use mock data if API fails
      setComponents([
        { name: 'ftp', title: 'FTP', description: 'Connect to FTP servers for file transfer' },
        { name: 'imaps', title: 'Email (IMAP)', description: 'Monitor email inbox via IMAP protocol' },
        { name: 'sftp', title: 'SFTP', description: 'Secure file transfer protocol connection' },
        { name: 'telegram', title: 'Telegram', description: 'Telegram bot integration for messages' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrations = async (componentName: string) => {
    try {
      setLoading(true);
      const data = await integrationsService.getComponentList(componentName);
      setIntegrations(data.content);
      setTotalIntegrations(data.totalElements);
    } catch (error) {
      console.error('Error loading integrations:', error);
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleComponentClick = (componentName: string) => {
    setSelectedComponent(componentName);
    setViewMode('list');
    loadIntegrations(componentName);
  };

  const handleBackToHome = () => {
    setViewMode('home');
    setSelectedComponent(null);
    setIntegrations([]);
    setSelectedIntegration(null);
  };

  const handleCreateIntegration = async () => {
    if (!newIntegration.name.trim() || !selectedComponent) return;

    try {
      // const integration = await integrationsService.createIntegration({
      //   name: newIntegration.name,
      //   description: newIntegration.description,
      //   componentName: selectedComponent,
      //   contentBasic: integrationsService.FORMIO_DEFAULT,
      //   contentAdvanced: integrationsService.FORMIO_DEFAULT,
      //   contentYaml: btoa(integrationsService.getDefaultYaml(selectedComponent)),
      // });

      toast.success('Integration created successfully');
      setShowCreateModal(false);
      setNewIntegration({ name: '', description: '' });
      loadIntegrations(selectedComponent);
    } catch (error) {
      toast.error('Failed to create integration');
    }
  };

  const handleDeleteIntegration = async () => {
    if (!selectedIntegration) return;

    try {
      await integrationsService.deleteIntegration(selectedIntegration.id);
      toast.success('Integration deleted successfully');
      setShowDeleteModal(false);
      setSelectedIntegration(null);
      if (selectedComponent) loadIntegrations(selectedComponent);
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const handleDeploy = async (integration: Integration) => {
    try {
      const fullYaml = btoa(integration.contentYaml || ''); // Simplified for now
      const result = await integrationsService.deployIntegration(integration.id, fullYaml);
      await integrationsService.patchIntegration(integration.id, result);
      toast.success('Deployed successfully!');
      if (selectedComponent) loadIntegrations(selectedComponent);
    } catch (error) {
      toast.error('Deployment failed');
    }
  };

  const handleStart = async (integration: Integration) => {
    if (!integration.containerId) {
      toast.error('Container not deployed yet');
      return;
    }
    try {
      await integrationsService.startContainer(integration.containerId);
      toast.success(`${integration.name} started`);
    } catch (error) {
      toast.error('Failed to start container');
    }
  };

  const handleStop = async (integration: Integration) => {
    if (!integration.containerId) return;
    try {
      await integrationsService.stopContainer(integration.containerId);
      toast.success(`${integration.name} stopped`);
    } catch (error) {
      toast.error('Failed to stop container');
    }
  };

  const handlePause = async (integration: Integration) => {
    if (!integration.containerId) return;
    try {
      await integrationsService.pauseContainer(integration.containerId);
      toast.success(`${integration.name} paused`);
    } catch (error) {
      toast.error('Failed to pause container');
    }
  };

  const handleViewLogs = async (integration: Integration) => {
    if (!integration.containerId) {
      toast.error('Container not deployed yet');
      return;
    }
    try {
      const logs = await integrationsService.getContainerLogs(integration.containerId);
      setLogs(logs.map(l => l.replace(/\s/g, '')).join('\n'));
      setShowLogsModal(true);
    } catch (error) {
      toast.error('Failed to fetch logs');
    }
  };

  const handleViewStatus = async (integration: Integration) => {
    if (!integration.containerId) {
      toast.error('Container not deployed yet');
      return;
    }
    try {
      const status = await integrationsService.getContainerStatus(integration.containerId);
      setCurrentStatus(`${integration.name} is ${status[0]?.status || 'unknown'}`);
      setShowStatusModal(true);
    } catch (error) {
      toast.error('Failed to fetch status');
    }
  };

  const filteredComponents = components.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render Home View - Zapier-style component grid
  const renderHomeView = () => (
    <div className="p-8">
      {/* Hero Section */}
      <div className="mb-10 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full text-sm font-medium mb-4">
          <Zap size={16} />
          Automation Platform
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3">
          Connect Your Apps
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Automate your workflows by connecting to external services.
          Set up triggers and actions to streamline your business processes.
        </p>
      </div>

      {/* Search */}
      <div className="max-w-xl mx-auto mb-10">
        <div className="relative">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search integrations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-2xl text-base focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 shadow-sm"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Plug size={24} className="text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{components.length}</div>
              <div className="text-sm text-gray-500">Available Connectors</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle size={24} className="text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">--</div>
              <div className="text-sm text-gray-500">Active Integrations</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Activity size={24} className="text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">--</div>
              <div className="text-sm text-gray-500">Events Today</div>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Components Grid */}
      <div className="max-w-5xl mx-auto">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Available Connectors</h2>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredComponents.map((component) => {
              const colors = componentColors[component.name] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' };
              return (
                <button
                  key={component.name}
                  onClick={() => handleComponentClick(component.name)}
                  className={`group p-6 ${colors.bg} border ${colors.border} rounded-2xl text-left hover:shadow-lg hover:scale-[1.02] transition-all duration-200`}
                >
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm group-hover:shadow-md transition-shadow">
                    {componentIcons[component.name] || <Plug size={24} className="text-gray-600" />}
                  </div>
                  <h3 className={`font-semibold ${colors.text} mb-1`}>{component.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{component.description}</p>
                  <div className="flex items-center gap-1 mt-4 text-sm font-medium text-gray-500 group-hover:text-gray-700">
                    <span>View integrations</span>
                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // Render List View - Integrations for a component
  const renderListView = () => {
    const colors = componentColors[selectedComponent || ''] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' };

    return (
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToHome}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowRight size={20} className="rotate-180 text-gray-500" />
            </button>
            <div className={`w-12 h-12 ${colors.bg} border ${colors.border} rounded-xl flex items-center justify-center`}>
              {componentIcons[selectedComponent || ''] || <Plug size={24} />}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {components.find(c => c.name === selectedComponent)?.title || selectedComponent} Integrations
              </h1>
              <p className="text-sm text-gray-500">{totalIntegrations} integration(s)</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors shadow-sm"
          >
            <Plus size={18} />
            New Integration
          </button>
        </div>

        {/* Integrations Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : integrations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <Plug size={48} className="mb-4 text-gray-300" />
              <p className="text-lg font-medium">No integrations yet</p>
              <p className="text-sm">Create your first integration to get started</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Trigger ID</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {integrations.map((integration) => (
                  <tr key={integration.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{integration.name}</div>
                      <div className="text-sm text-gray-500">{integration.description || 'No description'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {integration.triggerName || '--'}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${integration.containerId
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                        }`}>
                        {integration.containerId ? (
                          <><CheckCircle size={12} /> Deployed</>
                        ) : (
                          <><Clock size={12} /> Not deployed</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleDeploy(integration)}
                          className="p-2 hover:bg-blue-100 rounded-lg text-blue-600 transition-colors"
                          title="Deploy"
                        >
                          <ExternalLink size={16} />
                        </button>
                        <button
                          onClick={() => handleStart(integration)}
                          className="p-2 hover:bg-green-100 rounded-lg text-green-600 transition-colors"
                          title="Start"
                        >
                          <Play size={16} />
                        </button>
                        <button
                          onClick={() => handlePause(integration)}
                          className="p-2 hover:bg-yellow-100 rounded-lg text-yellow-600 transition-colors"
                          title="Pause"
                        >
                          <Pause size={16} />
                        </button>
                        <button
                          onClick={() => handleStop(integration)}
                          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                          title="Stop"
                        >
                          <Square size={16} />
                        </button>
                        <button
                          onClick={() => handleViewLogs(integration)}
                          className="p-2 hover:bg-purple-100 rounded-lg text-purple-600 transition-colors"
                          title="Logs"
                        >
                          <Terminal size={16} />
                        </button>
                        <button
                          onClick={() => handleViewStatus(integration)}
                          className="p-2 hover:bg-sky-100 rounded-lg text-sky-600 transition-colors"
                          title="Status"
                        >
                          <Info size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedIntegration(integration);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 hover:bg-red-100 rounded-lg text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          {/* Top Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-gradient-to-br from-orange-400 to-pink-500 rounded-xl">
                  <Plug size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Integrations</h1>
                  <p className="text-sm text-gray-500">Connect and automate external services</p>
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            {viewMode === 'home' && renderHomeView()}
            {viewMode === 'list' && renderListView()}
          </div>
        </div>
      </div>

      {/* Create Integration Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">New Integration</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newIntegration.name}
                  onChange={(e) => setNewIntegration({ ...newIntegration, name: e.target.value })}
                  placeholder="My Integration"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newIntegration.description}
                  onChange={(e) => setNewIntegration({ ...newIntegration, description: e.target.value })}
                  placeholder="Optional description"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateIntegration}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedIntegration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Delete Integration</h3>
              <button onClick={() => setShowDeleteModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to delete <span className="font-semibold">{selectedIntegration.name}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteIntegration}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Container Logs</h3>
              <button onClick={() => setShowLogsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-xl text-sm font-mono overflow-auto max-h-80">
                {logs || 'No logs available'}
              </pre>
            </div>
            <div className="flex justify-end px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowLogsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Container Status</h3>
              <button onClick={() => setShowStatusModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">{currentStatus}</p>
            </div>
            <div className="flex justify-end px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
